import cn from 'classnames';
import { useEffect, useState } from 'react';
import HubspotForm from 'react-hubspot-form';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import Image from 'next/image';
import Spinner from 'react-scale/components/elements/spinner';

import s from './form-content.module.scss';
import Button from 'react-scale/components/elements/button';

import LogoFarm from 'react-scale/components/elements/logo-grid';

const FORM_ID = '4cc8bca7-2fa4-420b-8afa-9d29d45acc3b';

const AutoSwitch = slider => {
  let timeout;
  let mouseOver = false;
  function clearNextTimeout() {
    clearTimeout(timeout);
  }
  function nextTimeout() {
    clearTimeout(timeout);
    if (mouseOver) return;
    timeout = setTimeout(() => {
      slider.next();
    }, 5000);
  }
  slider.on('created', () => {
    slider.container.addEventListener('mouseover', () => {
      mouseOver = true;
      clearNextTimeout();
    });
    slider.container.addEventListener('mouseout', () => {
      mouseOver = false;
      nextTimeout();
    });
    nextTimeout();
  });
  slider.on('dragStarted', clearNextTimeout);
  slider.on('animationEnded', nextTimeout);
  slider.on('updated', nextTimeout);
};

const FormContent = () => {
  const [opacities, setOpacities] = useState<number[]>([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      slides: images.length,
      loop: true,
      detailsChanged(s) {
        const new_opacities = s.track.details.slides.map(slide => slide.portion);
        setOpacities(new_opacities);
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      }
    },
    [AutoSwitch]
  );

  useEffect(() => {
    const handler = event => {
      if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormSubmitted') {
        if (event.data.id === FORM_ID) setFormSubmitted(true);
      }
    };

    window.addEventListener('message', handler);

    return () => {
      window.removeEventListener('message', handler);
    };
  }, []);

  useEffect(() => {
    if (formSubmitted) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [formSubmitted]);

  return (
    <div className="py-6 bg-transparent lg:py-20">
      <div className="container">
        <div className="grid grid-cols-1 gap-6 transition-all duration-700 ease-linear lg:grid-cols-2 lg:gap-7">
          <div
            className={cn(
              'relative flex flex-col justify-between',
              s.form__wrapper,
              formSubmitted && s.form__submitted
            )}
          >
            <div>
              <h1 className="text-3.5xl md:text-3xl leading-normal text-center lg:text-left tracking-tight text-black mb-4 md:mb-2">
                Let’s Scale Together
              </h1>
              <p className="mb-6 text-center text-neutral-800 lg:text-left md:text-sm 3xl:text-base">
                Join leading AI teams accelerating their ML development with Scale. Book a 1:1 demo
                with us to get started.
              </p>

              <HubspotForm
                portalId="4823527"
                formId={FORM_ID}
                submitButtonClass="hidden"
                loading={
                  <div className="flex items-center justify-center h-full">
                    <Spinner size="lg" />
                  </div>
                }
              />
            </div>

            <div className="mt-3 3xl:mt-5">
              {!formSubmitted && (
                // @ts-ignore
                <Button
                  text="Submit"
                  onClick={() => {
                    (
                      document.querySelector(
                        'div.hs-submit input[type="submit"]'
                      ) as HTMLButtonElement
                    )?.click();
                  }}
                />
              )}

              {/* <p className="text-sm mt-8 3xl:mt-[42px]">
                Looking for{' '}
                <Link href="mailto:chris@scale.com" className="inline-block text-scale-mauve">
                  partnerships?
                </Link>
              </p> */}
            </div>
          </div>

          <div className="bg-neutral-900 rounded-xl py-[42px] px-6 lg:px-9 lg:py-20 flex flex-col justify-between">
            <div>
              <div
                ref={sliderRef}
                className="rounded-[10px] p-[1px] aspect-[472/320] overflow-hidden mb-6 lg:mb-7 relative"
              >
                {images.map(({ src, alt }, idx) => (
                  <div
                    key={idx}
                    className="absolute top-0 w-full h-full"
                    style={{ opacity: opacities[idx] }}
                  >
                    <Image
                      src={src}
                      className="absolute object-cover w-full h-full"
                      alt={alt}
                      width={472}
                      height={320}
                      quality={100}
                    />
                  </div>
                ))}
              </div>
              <div className="flex space-x-3 lg:space-x-2 justify-center mb-16 lg:mb-18 h-[6px]">
                {loaded &&
                  instanceRef.current &&
                  [...Array(instanceRef.current.track.details.slides.length).keys()].map(idx => {
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          instanceRef.current?.moveToIdx(idx);
                        }}
                        className={cn(
                          'w-[6px] h-[6px] rounded-full',
                          currentSlide === idx ? 'bg-[#2b2b2b]' : 'bg-[#a6a6a6]'
                        )}
                      />
                    );
                  })}
              </div>

              <div className="mb-8 xl:mb-10">
                <Image
                  className="mb-4 xl:mb-6"
                  src="/static/images/logos/customers/goodcall.png"
                  width={127}
                  height={33}
                  alt="Flexport"
                  quality={100}
                />
                <q className="inline-block mb-6">
                  When it comes to model improvement, every percentage point counts. Improving
                  100,000 calls by 1% means that 1,000 people are getting a better experience. Every
                  percentage point is a victory, and with Scale, we’ve experienced 5% improvement
                  over just 3 months.
                </q>

                <div className="flex items-center space-x-4">
                  <Image
                    className="rounded-full"
                    src="/static/img/website/customers/avatars/bob-summers.jpeg"
                    width={50}
                    height={50}
                    alt="Scott Greg"
                    quality={100}
                  />

                  <div className="flex flex-col space-y-1">
                    <b>Bob Summers</b>
                    <p className="text-sm">CEO & Founder, Goodcall</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="mb-6 text-neutral-500">
                Trusted by the world's most ambitious AI teams
              </p>
              {/* @ts-ignore */}
              <LogoFarm
                limit={12}
                logos={[
                  'microsoft',
                  'brex',
                  'etsy',
                  'flexport',
                  'gm',
                  'instacart',
                  'meta',
                  'sap',
                  'square',
                  'toyota',
                  'airforce',
                  'usarmy'
                ]}
                columns={{ xs: 4, sm: 4, md: 6, lg: 6 }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormContent;

const images = [
  { src: '/static/images/pages/sales/rapid.png', alt: 'Scale Rapid project dashboard' },
  { src: '/static/images/pages/sales/compare.png', alt: 'Scale Forge generation results' },
  { src: '/static/images/pages/sales/spellbook.png', alt: 'Scale Spellbook comparison' }
];
